import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../lib/api-client';
import { formatMoney, penceToPoundsInput, poundsToPence, vatPenceFromPercent, vatRateValue } from '../lib/money';
import { useLinkedPreview } from '../lib/linked-scroll';
import { apiError } from '../lib/utils';
import { useLedgerReload } from '../lib/use-ledger';
import { takeDuplicate } from '../lib/duplicate';
import { peekPendingOfferFiles, takePendingOfferFiles } from '../lib/pending-offer-files';
import { loadedRecordKey, useEditGuard } from '../lib/dirty';
import { onOfferSaved } from '../lib/record-assist';
import { useAuth } from '../contexts/AuthContext';
import { useNotice } from '../components/ui/Notice';
import { EditWithAiButton, RecordAssistDialog } from '../components/records/RecordAssistDialog';
import { OfferWorkspace } from '../components/documents/OfferWorkspace';
import { LineTable } from '../components/documents/LineTable';
import { VatRateSelect } from '../components/documents/VatRateSelect';
import { IssueHistory, MarkStatus, revisionLabel } from '../components/documents/IssueHistory';
import { QuotationFormat2 } from '../components/documents/QuotationFormat2';
import { QuotationPreview } from '../components/documents/QuotationPreview';
import { Alert } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Dialog } from '../components/ui/Dialog';
import { RecordLink, SectionCard } from '../components/ui/chrome';
import { Field, GrowingText, Input, Select, TextArea } from '../components/ui/Input';
import { customerChoices, useFreshRows } from '../lib/select-choices';
import { useOurContact } from '../lib/our-contact';
import { useTermsSeed } from '../lib/terms-template';
import { EmailTemplateSelect, SupplierTemplateSelect, TermsTemplateSelect } from '../components/documents/TemplateSelects';
import { deleteQuotationFile, filesOf, ProposalFileLinks, uploadQuotationFile, type ProposalFileLink } from '../components/documents/ProposalFiles';
import { AddOfferPartDialog } from '../components/documents/AddOfferPartDialog';
import { OfferDuplicateDialog } from '../components/documents/OfferDuplicateDialog';
import { ReviewChoiceDialog } from '../components/documents/ReviewChoiceDialog';

interface QLine { description: string; qty: string; pounds: string; cost: string }
interface ProposalOption { id: string; number: string; title: string }
interface QuotationLink { id: string; number: string; title: string; proposalId?: string }

export function QuotationPage({ recordId, initial, bare, linkedProposalId, paired, saveAll, registerSave, onDirty, linkProposalId, sectionTab, onSelectTab }: {
  recordId?: string;
  initial?: Record<string, unknown> | null;
  bare?: boolean;
  linkedProposalId?: string;
  paired?: boolean;
  saveAll?: () => Promise<boolean>;
  registerSave?: (save: () => Promise<boolean | void>) => void;
  onDirty?: (dirty: boolean) => void;
  linkProposalId?: { current: string };
  sectionTab?: string;
  onSelectTab?: (id: string) => void;
} = {}) {
  const { can } = useAuth();
  const params = useParams();
  const id = recordId ?? params.id;
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const feedback = useNotice();
  const [customers, reloadCustomers] = useFreshRows<{ id: string; code?: string; name: string; description?: string }>('/api/customers');
  const [row, setRow] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const numberTouched = useRef(false);
  const [docKind, setDocKind] = useState('services');
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [scope, setScope] = useState('');
  const [summary, setSummary] = useState('');
  const [validDays, setValidDays] = useState(60);
  const [issuedOn, setIssuedOn] = useState(new Date().toISOString().slice(0, 10));
  const [currency, setCurrency] = useState('GBP');
  const markCleanRef = useRef<() => void>(() => {});
  const [lines, setLines] = useState<QLine[]>([{ description: '', qty: '1', pounds: '', cost: '' }]);
  const [billToName, setBillToName] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  useOurContact(!id || id === 'new', (contact) => {
    setContactName((value) => value || contact.contactName);
    setContactRole((value) => value || contact.contactRole);
    setContactEmail((value) => value || contact.contactEmail);
    setContactPhone((value) => value || contact.contactPhone);
  });
  const [notice, setNotice] = useState('');
  const [layout, setLayout] = useState<'1' | '2'>('1');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [delivery, setDelivery] = useState('');
  const [acceptance, setAcceptance] = useState('');
  useTermsSeed('offer', (!id || id === 'new') && !initial && search.get('duplicate') !== '1', (row) => {
    setPaymentTerms((value) => value || row.paymentTerms);
    setDelivery((value) => value || row.delivery);
    setNotice((value) => value || row.notice);
    setAcceptance((value) => value || row.acceptance);
    if (row.validDays > 0) setValidDays((value) => value === 60 ? row.validDays : value);
  });
  const [vat, setVat] = useState('');
  const [recipients, setRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [saveState, setSaveState] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const { previewRef, onFormScroll } = useLinkedPreview();
  const [proposalId, setProposalId] = useState('');
  const [proposalStatus, setProposalStatus] = useState('');
  const [proposals, setProposals] = useState<ProposalOption[]>([]);
  const [quotations, setQuotations] = useState<QuotationLink[]>([]);
  const [conflict, setConflict] = useState<{ proposalId: string; other: QuotationLink; moveTo: string } | null>(null);
  const duplicateApplied = useRef(false);
  const [unsavedCopy, setUnsavedCopy] = useState(false);
  const [askAdd, setAskAdd] = useState(false);
  const [askDuplicate, setAskDuplicate] = useState(false);
  const [askReview, setAskReview] = useState(false);
  const [assist, setAssist] = useState<'create' | 'edit' | null>(null);
  const [files, setFiles] = useState<ProposalFileLink[]>([]);
  const [fileBusy, setFileBusy] = useState(false);
  const savedIdRef = useRef('');

  function applyQuotation(found: Record<string, unknown>, linkedProposal = String(found.proposalId || '')) {
    setCustomerId(String(found.customerId || ''));
    setDocumentNumber(String(found.number || ''));
    numberTouched.current = Boolean(found.number);
    setDocKind(String(found.docKind || 'services'));
    setTitle(String(found.title || ''));
    setPurpose(String(found.purpose || ''));
    setScope(String(found.scope || ''));
    setSummary(String(found.summary || ''));
    setValidDays(Number(found.validDays || 60));
    setIssuedOn(String(found.issuedOn || ''));
    setCurrency(String(found.currency || 'GBP').toUpperCase());
    setProposalId(linkedProposal);
    setLines(((found.lines as { description: string; qty?: number; amountPence: number; unitPence?: number; costPence?: number }[]) || []).map((line) => ({
      description: line.description,
      qty: String(line.qty || 1),
      pounds: penceToPoundsInput(line.unitPence ?? line.amountPence),
      cost: typeof line.costPence === 'number' ? penceToPoundsInput(line.costPence) : '',
    })));
    setBillToName(String(found.billToName || ''));
    setBillToAddress(String(found.billToAddress || ''));
    setBillToEmail(String(found.billToEmail || ''));
    setContactName(String(found.contactName || ''));
    setContactRole(String(found.contactRole || ''));
    setContactEmail(String(found.contactEmail || ''));
    setContactPhone(String(found.contactPhone || ''));
    setNotice(String(found.notice || ''));
    setLayout(found.layout === '2' ? '2' : '1');
    setPaymentTerms(String(found.paymentTerms || ''));
    setDelivery(String(found.delivery || ''));
    setAcceptance(String(found.acceptance || ''));
    const storedLines = (found.lines as { amountPence?: number }[]) || [];
    const net = storedLines.reduce((sum, line) => sum + Number(line.amountPence || 0), 0);
    setVat(vatRateValue(net, typeof found.vatPence === 'number' ? found.vatPence : null));
    setRecipients(Array.isArray(found.recipients) ? (found.recipients as string[]).join(', ') : '');
    setEmailSubject(String(found.emailSubject || ''));
    setEmailBody(String(found.emailBody || ''));
    setFiles(filesOf(found));
  }

  async function reload() {
    if (initial) {
      if (duplicateApplied.current) return;
      duplicateApplied.current = true;
      setRow(null);
      setUnsavedCopy(true);
      applyQuotation({ ...initial, title: '' }, linkedProposalId || '');
      return;
    }
    if (!id || id === 'new') {
      if (duplicateApplied.current) return;
      setRow(null);
      if (search.get('duplicate') === '1') {
        const copy = takeDuplicate('quotation');
        if (copy) {
          duplicateApplied.current = true;
          setUnsavedCopy(true);
          applyQuotation(copy, search.get('proposalId') || '');
          return;
        }
      }
      setCustomerId(search.get('customerId') || '');
      setTitle(search.get('title') || '');
      setProposalId(search.get('proposalId') || '');
      setCurrency('GBP');
      setLayout('1');
      setPaymentTerms('');
      setDelivery('');
      setAcceptance('');
      setVat('');
      setDocumentNumber('');
      numberTouched.current = false;
      return;
    }
    const list = await apiGet<Record<string, unknown>[]>('/api/quotations');
    const found = list.find((item) => item.id === id) || null;
    setRow(found);
    if (!found) return;
    applyQuotation(found);
  }

  useEffect(() => {
    if (!proposalId) {
      setProposalStatus('');
      return;
    }
    void apiGet<{ status?: string }>(`/api/proposals/${proposalId}`).then((found) => setProposalStatus(String(found.status || ''))).catch(() => setProposalStatus(''));
  }, [proposalId]);

  useEffect(() => onOfferSaved((ids) => {
    if (ids.quotationId && ids.quotationId === String(row?.id || '')) void reload();
  }), [row?.id]);

  useEffect(() => {
    void apiGet<{ id: string; number?: string; title?: string; kind?: string }[]>('/api/commercial').then((rows) => {
      setProposals(rows.filter((item) => item.kind === 'proposal').map((item) => ({ id: item.id, number: String(item.number || ''), title: String(item.title || '') })));
    }).catch(() => setProposals([]));
    void apiGet<QuotationLink[]>('/api/quotations').then((rows) => setQuotations(rows.map((item) => ({ id: item.id, number: String(item.number || ''), title: String(item.title || ''), proposalId: item.proposalId || '' })))).catch(() => setQuotations([]));
    void reload().catch((err) => setError(apiError(err, 'Could not load')));
  }, [id]);

  useEffect(() => {
    if (row?.id || numberTouched.current || !customerId) return;
    const handle = window.setTimeout(() => {
      void apiGet<{ number: string }>(`/api/quotations/next-number?customerId=${encodeURIComponent(customerId)}&docKind=${encodeURIComponent(docKind)}`)
        .then((next) => { if (!numberTouched.current && next.number) setDocumentNumber(next.number); })
        .catch(() => undefined);
    }, 200);
    return () => window.clearTimeout(handle);
  }, [customerId, docKind, row?.id]);

  useLedgerReload('quotations', () => { void reload(); });

  const total = lines.reduce((sum, line) => sum + Math.round((Number(line.qty) || 1) * poundsToPence(line.pounds)), 0);
  const internalCost = lines.reduce((sum, line) => sum + poundsToPence(line.cost), 0);
  const locked = row?.status === 'issued' || row?.status === 'accepted';

  function chooseProposal(nextId: string) {
    const other = quotations.find((item) => item.proposalId === nextId && item.id !== id);
    if (nextId && other) {
      setConflict({ proposalId: nextId, other, moveTo: '' });
      return;
    }
    setProposalId(nextId);
  }

  async function resolveConflict(moveTo: string) {
    if (!conflict) return;
    setError('');
    try {
      await apiPut(`/api/quotations/${conflict.other.id}`, { proposalId: moveTo });
      if (row?.id) await apiPut(`/api/quotations/${row.id}`, { proposalId: conflict.proposalId });
      setQuotations((rows) => rows.map((item) => {
        if (item.id === conflict.other.id) return { ...item, proposalId: moveTo };
        if (item.id === id) return { ...item, proposalId: conflict.proposalId };
        return item;
      }));
      setProposalId(conflict.proposalId);
      setConflict(null);
      feedback.success('Quotation moved');
    } catch (err) {
      feedback.failure(apiError(err, 'Could not move the other quotation'));
    }
  }

  function body() {
    return {
      customerId, title, purpose, scope, summary, validDays, issuedOn, proposalId: linkProposalId?.current || proposalId, docKind, currency,
      ...(row?.id || !documentNumber ? {} : { number: documentNumber }),
      billToName, billToAddress, billToEmail, contactName, contactRole, contactEmail, contactPhone, notice,
      layout, paymentTerms, delivery, acceptance,
      vatPence: vatPenceFromPercent(total, vat),
      emailSubject, emailBody,
      recipients: recipients.split(/[,;\n]+/).map((item) => item.trim()).filter(Boolean),
      lines: lines.filter((line) => line.description || line.pounds).map((line) => {
        const qty = Number(line.qty) || 1;
        const unitPence = poundsToPence(line.pounds);
        return { description: line.description, qty, unitPence, amountPence: Math.round(qty * unitPence), costPence: line.cost ? poundsToPence(line.cost) : undefined };
      }),
    };
  }

  async function save(options?: { stay?: boolean }) {
    if (locked) return true;
    setError('');
    setSaveState('saving');
    try {
      const creating = !row?.id;
      let saved = creating
        ? await apiPost<Record<string, unknown>>('/api/quotations', body())
        : await apiPut<Record<string, unknown>>(`/api/quotations/${row.id}`, body());
      if (creating && search.get('attach') === '1' && saved.id) {
        for (const file of takePendingOfferFiles('quotation')) saved = await uploadQuotationFile(String(saved.id), file);
      }
      setRow(saved);
      setFiles(filesOf(saved));
      markCleanRef.current();
      setSaveState('saved');
      feedback.success('Saved');
      const alone = bare && paired && !proposalId && !linkProposalId?.current;
      savedIdRef.current = saved.id ? String(saved.id) : '';
      if (!options?.stay && !recordId && saved.id && (alone || (!bare && !paired))) navigate(`/quotations/${saved.id}`);
      return true;
    } catch (err) {
      setSaveState('failed');
      feedback.failure(apiError(err, 'Save failed'));
      return false;
    }
  }

  registerSave?.(() => save());
  const guardKey = loadedRecordKey(id, row?.id ? String(row.id) : undefined) || ((initial || paired) && !row?.id ? 'new' : '');
  markCleanRef.current = useEditGuard(guardKey, JSON.stringify(body()), save, paired ? { silent: true, onDirty } : undefined);

  async function addFile(file: File) {
    setFileBusy(true);
    try {
      const existed = Boolean(row?.id);
      if (!existed) {
        const ok = await save({ stay: true });
        if (!ok || !savedIdRef.current) return;
      }
      const savedId = existed ? String(row?.id) : savedIdRef.current;
      const saved = await uploadQuotationFile(savedId, file);
      setRow(saved);
      setFiles(filesOf(saved));
      feedback.success('File attached');
      const alone = bare && paired && !proposalId && !linkProposalId?.current;
      if (!existed && !recordId && (alone || (!bare && !paired))) navigate(`/quotations/${savedId}`);
    } catch (err) {
      feedback.failure(apiError(err, 'Could not attach that file'));
    } finally {
      setFileBusy(false);
    }
  }

  async function removeFile(path: string) {
    if (!row?.id) {
      setFiles((current) => current.filter((file) => file.path !== path));
      return;
    }
    setFileBusy(true);
    try {
      const saved = await deleteQuotationFile(String(row.id), path);
      setRow(saved);
      setFiles(filesOf(saved));
    } catch (err) {
      feedback.failure(apiError(err, 'Could not remove that file'));
    } finally {
      setFileBusy(false);
    }
  }

  async function revise() {
    try {
      const saved = await apiPost<{ id: string }>(`/api/quotations/${row?.id}/revise`, {});
      navigate(`/quotations/${saved.id}`);
      feedback.success('Revision created');
    } catch (err) {
      feedback.failure(apiError(err, 'Revise failed'));
    }
  }

  const tab = sectionTab || search.get('tab') || 'quotation';
  const customerName = customers.find((item) => item.id === customerId)?.name || '';
  const vatPence = vatPenceFromPercent(total, vat);
  const preview = layout === '2' ? (
    <QuotationFormat2 number={documentNumber || (row?.number ? String(row.number) : undefined)} customerName={customerName} title={title} issuedOn={issuedOn} currency={currency} validDays={validDays} billToName={billToName} billToAddress={billToAddress} billToEmail={billToEmail} contactName={contactName} contactRole={contactRole} contactEmail={contactEmail} contactPhone={contactPhone} notice={notice} scope={scope} paymentTerms={paymentTerms} delivery={delivery} acceptance={acceptance} lines={lines} vatPence={vatPence} />
  ) : (
    <QuotationPreview number={documentNumber || (row?.number ? String(row.number) : undefined)} customerName={customerName} title={title} issuedOn={issuedOn} currency={currency} validDays={validDays} billToName={billToName} billToAddress={billToAddress} billToEmail={billToEmail} contactName={contactName} contactRole={contactRole} contactEmail={contactEmail} contactPhone={contactPhone} notice={notice} scope={scope} paymentTerms={paymentTerms} delivery={delivery} acceptance={acceptance} lines={lines} vatPence={vatPence} />
  );

  useEffect(() => {
    if (linkedProposalId) setProposalId(linkedProposalId);
  }, [linkedProposalId]);

  const formView = (
    <>
      {error ? <Alert error>{error}</Alert> : null}
      {unsavedCopy ? <p className="mb-3 text-sm text-[var(--bo-muted)]">Not saved yet. Save creates a new quotation.</p> : null}
      {tab === 'activity' ? <IssueHistory issues={row?.issues} sharePointUrl={typeof row?.sharePointUrl === 'string' ? row.sharePointUrl : ''} /> : (
      <div className={`grid h-[calc(100vh-16rem)] items-stretch gap-4 ${previewing ? 'xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'xl:grid-cols-[minmax(0,1fr)_22rem]'}`}>
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1" onScroll={previewing ? onFormScroll : undefined}>
          <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] px-4 py-3">
            <div className="text-xs text-[var(--bo-muted)]">Total</div>
            <div className="text-lg font-semibold tabular-nums">{formatMoney(vatPence == null ? total : total + vatPence, currency)}</div>
            <div className="text-xs text-[var(--bo-faint)]">Net {formatMoney(total, currency)} · VAT {vatPence == null ? 'not set' : formatMoney(vatPence, currency)}. Read only.</div>
          </div>
          <SectionCard title="Quotation" hint="The kind sets the number. The number can be edited until the first save.">
          <div className="grid gap-3 lg:grid-cols-2">
          <Field label="Format"><Select value={layout} disabled={locked} onChange={(event) => setLayout(event.target.value === '2' ? '2' : '1')}><option value="1">Traditional</option><option value="2">Modern</option></Select></Field>
          <Field label="Customer"><Select searchable choices={customerChoices(customers)} onOpen={reloadCustomers} value={customerId} disabled={locked} onChange={(event) => setCustomerId(event.target.value)} /></Field>
          <Field label="Kind"><Select value={docKind} disabled={locked} onChange={(event) => setDocKind(event.target.value)}><option value="services">Services</option><option value="licence">Licences</option><option value="support">Support and cloud</option><option value="commercial">Commercial</option></Select></Field>
          <Field label="Quotation number"><Input value={documentNumber || (row?.number ? String(row.number) : '')} disabled={Boolean(row?.id) || locked} onChange={(event) => { numberTouched.current = true; setDocumentNumber(event.target.value); }} /></Field>
          <Field label="Title"><Input value={title} disabled={locked} onChange={(event) => setTitle(event.target.value)} /></Field>
          <Field label="Currency"><Select value={currency} disabled={locked} onChange={(event) => setCurrency(event.target.value)}><option value="GBP">GBP</option><option value="EUR">EUR</option><option value="USD">USD</option></Select></Field>
          <Field label="Issue date"><Input type="date" value={issuedOn} disabled={locked} onChange={(event) => setIssuedOn(event.target.value)} /></Field>
          <Field label="Valid days"><Input type="number" value={validDays} disabled={locked} onChange={(event) => setValidDays(Number(event.target.value))} /></Field>
          <Field label="Proposal">
            <Select searchable value={proposalId} disabled={locked} onChange={(event) => chooseProposal(event.target.value)}>
              <option value="">None</option>
              {proposals.map((proposal) => <option key={proposal.id} value={proposal.id}>{proposal.number} {proposal.title}</option>)}
            </Select>
            {proposalId ? <RecordLink className="mt-1 inline-block text-sm text-[var(--bo-link)]" to={`/proposals/${proposalId}`} backLabel={`Back to ${documentNumber || 'quotation'}`}>Open proposal</RecordLink> : null}
          </Field>
          </div>
          </SectionCard>
          <div className="grid gap-4 lg:grid-cols-2">
            <SectionCard title="Client Details" hint="Who the quotation is addressed to.">
              <div className="space-y-3">
                <Field label="Bill to"><Input value={billToName} disabled={locked} onChange={(event) => setBillToName(event.target.value)} /></Field>
                <Field label="Address"><GrowingText value={billToAddress} disabled={locked} onChange={setBillToAddress} /></Field>
                <Field label="Email"><Input value={billToEmail} disabled={locked} onChange={(event) => setBillToEmail(event.target.value)} /></Field>
              </div>
            </SectionCard>
            <SectionCard title="Supplier Details" hint="The contact printed on the quotation.">
              <div className="space-y-3">
                <SupplierTemplateSelect onApply={(contact) => { setContactName(contact.contactName); setContactRole(contact.contactRole); setContactEmail(contact.contactEmail); setContactPhone(contact.contactPhone); }} />
                <Field label="Contact"><Input value={contactName} disabled={locked} onChange={(event) => setContactName(event.target.value)} /></Field>
                <Field label="Role"><Input value={contactRole} disabled={locked} onChange={(event) => setContactRole(event.target.value)} /></Field>
                <Field label="Email"><Input value={contactEmail} disabled={locked} onChange={(event) => setContactEmail(event.target.value)} /></Field>
                <Field label="Telephone"><Input value={contactPhone} disabled={locked} onChange={(event) => setContactPhone(event.target.value)} /></Field>
              </div>
            </SectionCard>
          </div>
          <SectionCard title="Email" hint="This text is used on Review and issue.">
            <div className="space-y-3">
              <EmailTemplateSelect kind="quotation" values={{ number: documentNumber || (row?.number ? String(row.number) : ''), customer: customerName, title }} onApply={(subject, message) => { setEmailSubject(subject); setEmailBody(message); }} />
              <Field label="Subject"><Input value={emailSubject} disabled={locked} onChange={(event) => setEmailSubject(event.target.value)} /></Field>
              <Field label="Body"><GrowingText value={emailBody} disabled={locked} onChange={setEmailBody} /></Field>
            </div>
          </SectionCard>
          <SectionCard title="Line items" hint="Items and services included in this quotation.">
          <LineTable
            columns={['Description', 'Qty', 'Unit price', 'Internal cost', 'Amount']}
            count={lines.length}
            locked={locked}
            addLabel="Add line item"
            onAdd={() => setLines((rows) => [...rows, { description: '', qty: '1', pounds: '', cost: '' }])}
            onRemove={(index) => setLines((rows) => rows.filter((_, i) => i !== index))}
            render={(index) => {
              const line = lines[index];
              return [
                <Input disabled={locked} value={line.description} onChange={(event) => setLines((rows) => rows.map((item, i) => i === index ? { ...item, description: event.target.value } : item))} />,
                <Input className="w-16" type="number" disabled={locked} value={line.qty} onChange={(event) => setLines((rows) => rows.map((item, i) => i === index ? { ...item, qty: event.target.value } : item))} />,
                <Input className="w-full md:w-28" type="number" step="0.01" disabled={locked} value={line.pounds} onChange={(event) => setLines((rows) => rows.map((item, i) => i === index ? { ...item, pounds: event.target.value } : item))} />,
                <Input className="w-full md:w-28" type="number" step="0.01" disabled={locked} value={line.cost} placeholder="Internal" onChange={(event) => setLines((rows) => rows.map((item, i) => i === index ? { ...item, cost: event.target.value } : item))} />,
                <span className="tabular-nums">{formatMoney(Math.round((Number(line.qty) || 1) * poundsToPence(line.pounds)), currency)}</span>,
              ];
            }}
          />
          <p className="mt-2 text-xs text-[var(--bo-faint)]">Internal cost {formatMoney(internalCost, currency)}. Margin {formatMoney(total - internalCost, currency)}. This stays off the customer document.</p>
          </SectionCard>
          {previewing ? (
          <SectionCard title="Quotation summary" hint="Totals and tax.">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span className="tabular-nums">{formatMoney(total, currency)}</span></div>
            <div className="mt-2 flex justify-between text-sm"><span>VAT</span><span>{vatPence == null ? 'Not configured' : formatMoney(vatPence, currency)}</span></div>
            <div className="mt-3 flex justify-between border-t border-[var(--bo-border)] pt-3 font-semibold"><span>{vatPence == null ? 'Total excl. VAT' : 'Total'}</span><span className="tabular-nums">{formatMoney(vatPence == null ? total : total + vatPence, currency)}</span></div>
            {vatPence == null ? <p className="mt-3 text-sm text-amber-200">Choose a VAT treatment before issuing.</p> : null}
            <VatRateSelect value={vat} disabled={locked} onChange={setVat} />
          </SectionCard>
          ) : null}
          <SectionCard title="Commercial terms" hint="Agreed terms for this quotation.">
          <TermsTemplateSelect kind="offer" onApply={(row) => { setPaymentTerms(row.paymentTerms); setDelivery(row.delivery); setNotice(row.notice); setAcceptance(row.acceptance); if (row.validDays > 0) setValidDays(row.validDays); }} />
          <div className="grid gap-3 lg:grid-cols-2">
          <Field label="Payment terms"><TextArea value={paymentTerms} disabled={locked} onChange={(event) => setPaymentTerms(event.target.value)} /></Field>
          <Field label="Delivery"><TextArea value={delivery} disabled={locked} onChange={(event) => setDelivery(event.target.value)} /></Field>
          <Field label="Notice / Terms"><TextArea value={notice} disabled={locked} placeholder="[Terms and Conditions](https://maxiron.com/terms-and-conditions/)" onChange={(event) => setNotice(event.target.value)} /></Field>
          <Field label="To proceed"><TextArea value={acceptance} disabled={locked} onChange={(event) => setAcceptance(event.target.value)} /></Field>
          <Field label="Purpose"><TextArea value={purpose} disabled={locked} onChange={(event) => setPurpose(event.target.value)} /></Field>
          <Field label="Scope"><TextArea value={scope} disabled={locked} onChange={(event) => setScope(event.target.value)} /></Field>
          </div>
          </SectionCard>
        </div>
        <div ref={previewing ? previewRef : undefined} className="min-h-0 space-y-4 overflow-y-auto">
          {previewing ? <div className="rounded-lg bg-[var(--bo-preview)] p-3">{preview}</div> : (
            <>
              <SectionCard title="Quotation summary" hint="Totals and tax.">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span className="tabular-nums">{formatMoney(total, currency)}</span></div>
                <div className="mt-2 flex justify-between text-sm"><span>VAT</span><span>{vatPence == null ? 'Not configured' : formatMoney(vatPence, currency)}</span></div>
                <div className="mt-3 flex justify-between border-t border-[var(--bo-border)] pt-3 font-semibold"><span>{vatPence == null ? 'Total excl. VAT' : 'Total'}</span><span className="tabular-nums">{formatMoney(vatPence == null ? total : total + vatPence, currency)}</span></div>
                {vatPence == null ? <p className="mt-3 text-sm text-amber-200">Choose a VAT treatment before issuing.</p> : null}
                <VatRateSelect value={vat} disabled={locked} onChange={setVat} />
              </SectionCard>
              <SectionCard title="Extra documents" hint="Attach more files in any format. They are sent with the quotation and stored in SharePoint.">
                {search.get('attach') === '1' && peekPendingOfferFiles('quotation').length ? <p className="text-sm text-[var(--bo-muted)]">Attached when you save: {peekPendingOfferFiles('quotation').map((file) => file.name).join(', ')}</p> : null}
                <ProposalFileLinks
                  files={files}
                  locked={row?.status === 'accepted'}
                  busy={fileBusy}
                  onPick={(file) => void addFile(file)}
                  onRemove={(path) => void removeFile(path)}
                />
              </SectionCard>
              <SectionCard title="Linked proposal">
                {proposalId ? <RecordLink className="text-sm text-[var(--bo-link)]" to={`/proposals/${proposalId}`} backLabel={`Back to ${documentNumber || 'quotation'}`}>Open proposal</RecordLink> : row?.id ? <button type="button" className="text-sm text-[var(--bo-link)]" onClick={() => setAskAdd(true)}>Add proposal</button> : <p className="text-sm text-[var(--bo-muted)]">Save the quotation, then add a proposal.</p>}
              </SectionCard>
            </>
          )}
        </div>
      </div>
      )}
      {conflict ? (
        <Dialog title="Proposal already linked" onClose={() => setConflict(null)} footer={
          <>
            <Button variant="outline" onClick={() => void resolveConflict('')}>Leave it unlinked</Button>
            <Button onClick={() => void resolveConflict(conflict.moveTo)} disabled={!conflict.moveTo}>Move and link</Button>
          </>
        }>
          <p className="text-sm text-gray-600 dark:text-gray-300">{conflict.other.number} {conflict.other.title} is already linked to this proposal. Leave that quotation unlinked, or move it to another proposal. This quotation takes the proposal either way.</p>
          <Field label="Move the other quotation to">
            <Select searchable value={conflict.moveTo} onChange={(event) => setConflict({ ...conflict, moveTo: event.target.value })}>
              <option value="">Select a proposal</option>
              {proposals.filter((proposal) => proposal.id !== conflict.proposalId && !quotations.some((item) => item.proposalId === proposal.id && item.id !== conflict.other.id)).map((proposal) => <option key={proposal.id} value={proposal.id}>{proposal.number} {proposal.title}</option>)}
            </Select>
          </Field>
        </Dialog>
      ) : null}
    </>
  );
  return (
    <>
      {bare ? (
        <div className="space-y-3">
          {saveAll ? null : <div className="flex justify-end gap-2">{row?.id ? <EditWithAiButton kind="quotation" status={row.status} onClick={() => setAssist('edit')} /> : <Button variant="outline" onClick={() => setAssist('create')}>Prepare with AI</Button>}<Button onClick={() => void save()}>Save</Button></div>}
          {formView}
        </div>
      ) : (
        <OfferWorkspace
          number={documentNumber || (row?.number ? String(row.number) : undefined)}
          title={title}
          customer={customerName}
          status={row?.poId ? 'committed' : row?.status ? String(row.status) : 'draft'}
          orderTo={row?.poId ? `/purchase-orders/${row.poId}` : undefined}
          orderLabel={row?.poNumber ? String(row.poNumber) : undefined}
          revision={row ? revisionLabel(row.revision).replace('Revision ', '') : '1'}
          saveState={saveState}
          tab={tab === 'activity' || tab === 'overview' ? tab : 'quotation'}
          proposalTo={onSelectTab ? undefined : (proposalId ? `/proposals/${proposalId}` : undefined)}
          quotationTo={onSelectTab ? undefined : (row?.id ? `/quotations/${row.id}` : undefined)}
          onSelectTab={onSelectTab}
          onMissing={onSelectTab || proposalId || !row?.id ? undefined : (part) => { if (part === 'proposal') setAskAdd(true); }}
          actions={
            <>
              {row?.id ? <EditWithAiButton kind="quotation" status={row.status} onClick={() => setAssist('edit')} /> : <Button variant="outline" onClick={() => setAssist('create')}>Prepare with AI</Button>}
              <Button variant="outline" onClick={() => setPreviewing((value) => !value)}>{previewing ? 'Edit' : 'Preview'}</Button>
              {row?.id && !proposalId && !onSelectTab ? <Button variant="outline" onClick={() => setAskAdd(true)}>Add proposal</Button> : null}
              {locked && !paired ? null : <Button onClick={() => void (saveAll ? saveAll() : save())}>Save</Button>}
              {row?.id && row.status === 'draft' && can('quotations', 'issue') ? <Button onClick={() => void (async () => { const ok = saveAll ? await saveAll() : await save(); if (ok !== false) setAskReview(true); })()}>Review & issue</Button> : null}
              {row?.id ? <DropdownMenu trigger={<Button variant="outline" aria-label="More"><MoreHorizontal className="h-4 w-4" /></Button>} items={[
                { label: 'Duplicate', onClick: () => setAskDuplicate(true) },
                { label: 'PDF', onClick: () => window.open(`/api/quotations/${row.id}/pdf`, '_blank') },
                ...(row?.status === 'issued' ? [{ label: 'Revise', onClick: () => void revise() }] : []),
              ]} /> : null}
              <MarkStatus id={row?.id ? String(row.id) : undefined} url="/api/quotations" status={row?.status ? String(row.status) : undefined} onDone={setRow} />
            </>
          }
        >
          {formView}
        </OfferWorkspace>
      )}
      {assist ? <RecordAssistDialog kind={proposalId ? 'sales-offer' : 'quotation'} mode={assist} recordId={row?.id ? String(row.id) : undefined} proposalId={proposalId || undefined} quotationId={row?.id ? String(row.id) : undefined} proposalStatus={proposalStatus} quotationStatus={row?.status ? String(row.status) : undefined} current={proposalId ? { quotation: body() } : body()} title={proposalId ? 'Offer' : 'Quotation'} onClose={() => setAssist(null)} onSaved={(saved) => { const next = String(saved.quotationId || ''); if (!row?.id && !recordId) { const open = next || String(saved.proposalId || saved.id || ''); if (open) navigate(next ? `/quotations/${open}` : `/proposals/${open}`); return; } if (next) void reload(); }} /> : null}
      {askAdd && row?.id ? <AddOfferPartDialog kind="proposal" customerId={customerId} title={title} quotationId={String(row.id)} onClose={() => setAskAdd(false)} /> : null}
      {askDuplicate ? <OfferDuplicateDialog proposalId={proposalId || undefined} quotationId={row?.id ? String(row.id) : undefined} onClose={() => setAskDuplicate(false)} /> : null}
      {askReview && row?.id ? <ReviewChoiceDialog reviewId={String(row.id)} proposalId={proposalId || undefined} quotationId={String(row.id)} onClose={() => setAskReview(false)} /> : null}
    </>
  );
}
